import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { HiOutlineCheckBadge, HiOutlineCreditCard, HiOutlineShieldCheck, HiOutlineWallet } from "react-icons/hi2";
import { FaRegUser, FaEnvelope, FaPhone, FaClock } from "react-icons/fa";
import { baseurl } from "../../baseurl";

const HotelCommit = () => {
  const { state } = useLocation();
  const navigate = useNavigate();

  // Redirect if no state (page refresh protection)
  useEffect(() => {
    if (!state) navigate("/");
  }, [state, navigate]);

  const { hotelInfo, roomDetails, guestDetails, token, markupApplied } = state || {};

  // Payment & Blocking States
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isBlocking, setIsBlocking] = useState(true);
  const [blockError, setBlockError] = useState(null);
  const [blockRoomId, setBlockRoomId] = useState(null);
  
  const userId = localStorage.getItem("guestUserId");

  // --- FARE CALCULATIONS ---
  
  // 1. Calculate Booking Price (Base + your admin markup)
  const calculateTotalWithMarkup = () => {
    const baseFare = roomDetails?.Price?.PublishedPriceRoundedOff || 0;
    if (!markupApplied) return baseFare;

    const markupAmt = parseFloat(markupApplied.amount) || 0;
    const type = markupApplied.type?.toLowerCase();

    let total = 0;
    if (type === "percent" || type === "percentage") {
      total = baseFare + (baseFare * (markupAmt / 100));
    } else {
      total = baseFare + markupAmt;
    }
    return Math.round(total);
  };

  const bookingTotal = calculateTotalWithMarkup();
  const appliedWalletAmount = useWallet ? Math.min(walletBalance, bookingTotal) : 0;
  
  // 2. Calculate Gateway Fee (2.7% only on the amount NOT covered by wallet)
  const gatewayBaseAmount = bookingTotal - appliedWalletAmount;
  const gatewayFee = gatewayBaseAmount > 0 ? Math.round(gatewayBaseAmount * 0.027) : 0;
  
  // 3. Final Net Payable
  const finalNetPayable = gatewayBaseAmount + gatewayFee;

  // 1. Block Room on Component Mount
  useEffect(() => {
    const blockHotelRoom = async () => {
      try {
        setIsBlocking(true);
        const res = await axios.post(`${baseurl}/blockRoomGuest`, {
          hotelToken: token,
          RoomUniqueId: roomDetails.RoomUniqueId
        });

        if (res.data.status === "success" && res.data.data.Status === 1) {
          const bId = res.data.data.BlockRoom.BlockRoomResult.BlockRoomId;
          setBlockRoomId(bId);
          setIsBlocking(false);
        } else {
          setBlockError(res.data.data?.Message || "The room is no longer available at this price.");
        }
      } catch (err) {
        setBlockError("Failed to verify room availability. Please try again.");
      } finally {
        setIsBlocking(false);
      }
    };

    if (token && roomDetails?.RoomUniqueId) {
      blockHotelRoom();
    }
  }, [token, roomDetails]);

  // 2. Fetch Wallet Balance
  useEffect(() => {
    if (userId) {
      axios.get(`${baseurl}/getWalletBalance/${userId}`)
        .then(res => setWalletBalance(parseFloat(res.data.balance) || 0))
        .catch(err => console.error("Wallet Error", err));
    }
  }, [userId]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleFinalPayment = async () => {
    setIsProcessing(true);

    const formattedRoomDetails = [
      {
        PassengerDetails: guestDetails.guests.map((guest, index) => ({
          Title: guest.title,
          FirstName: guest.firstName,
          MiddleName: "",
          LastName: guest.lastName,
          Phoneno: guestDetails.phoneNumber,
          Email: guestDetails.email,
          PaxType: guest.type === "Adult" ? "1" : "2",
          LeadPassenger: index === 0,
          Age: guest.age || (guest.type === "Adult" ? 30 : 10),
        })),
      },
    ];

    const bookingPayload = {
      userId,
      hotelToken: token,
      blockRoomId: blockRoomId,
      roomUniqueId: roomDetails.RoomUniqueId,
      guestDetails: guestDetails,
      totalFare: bookingTotal, 
      appliedWalletAmount,
      remainingAmount: finalNetPayable, // Amount Razorpay handles (inc. fee)
      isSplitPayment: appliedWalletAmount > 0 && finalNetPayable > 0,
      markupApplied
    };

    try {
      // CASE: Full Wallet Payment (No gateway fee applies)
      if (useWallet && bookingTotal <= walletBalance) {
        const res = await axios.post(`${baseurl}/payHotelViaWalletOnly`, bookingPayload);
        if (res.data.status === 'success') {
          navigate("/booking-success", { state: { booking: res.data.booking } });
        } else {
          alert(res.data.message || "Wallet transaction failed.");
        }
        return;
      }

      // CASE: Razorpay (Full or Partial)
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        alert("Razorpay SDK failed to load.");
        setIsProcessing(false);
        return;
      }

      const orderRes = await axios.post(`${baseurl}/createHotelOrder`, {
        amount: finalNetPayable,
        userId,
        hotelToken: token,
        blockRoomId: blockRoomId,
        walletUsed: appliedWalletAmount,
        totalFare: bookingTotal,
        guestDetails: guestDetails ,
        markupApplied
      });

      const options = {
        key: "rzp_test_A8i5qZiJOeIYx8", 
        amount: orderRes.data.amount,
        currency: "INR",
        name: "Hotel Booking",
        description: `Booking at ${hotelInfo.HotelName}`,
        order_id: orderRes.data.id,
        handler: async function (response) {
          const verificationData = {
            ...response,
            hotelToken: token,
            blockRoomId: blockRoomId,
            userId,
            walletUsed: appliedWalletAmount,
            amountPaid: finalNetPayable,
            RoomDetails: formattedRoomDetails,
          };

          const verifyRes = await axios.post(`${baseurl}/verifyHotelPayment`, verificationData);
          if (verifyRes.data.status === 'success') {
            navigate("/booking-success", { state: { booking: verifyRes.data.booking } });
          } else {
            alert("Booking verification failed.");
          }
        },
        prefill: { email: guestDetails.email, contact: guestDetails.phoneNumber },
        theme: { color: "#0d6efd" },
        modal: { ondismiss: () => setIsProcessing(false) }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error("❌ Payment Error:", err);
      alert("Transaction failed.");
    } finally {
      if (useWallet && bookingTotal <= walletBalance) setIsProcessing(false);
    }
  };

  if (isBlocking) return (
    <div className="vh-100 d-flex flex-column justify-content-center align-items-center bg-white">
      <div className="spinner-border text-primary mb-3" role="status"></div>
      <h5 className="fw-bold">Securing your room...</h5>
      <p className="text-muted small">Finalizing price with provider.</p>
    </div>
  );

  if (blockError) return (
    <div className="container mt-5 text-center">
      <div className="alert alert-warning p-5 shadow-sm rounded-4">
        <h4 className="fw-bold text-danger">Booking Expired</h4>
        <p className="text-muted">{blockError}</p>
        <button onClick={() => navigate(-1)} className="btn btn-primary rounded-pill px-4 mt-3">Go Back & Re-select</button>
      </div>
    </div>
  );

  return (
    <section className="bg-light min-vh-100 py-5">
      <div className="container">
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm rounded-4 mb-4">
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="fw-bold mb-0 text-primary"><HiOutlineCheckBadge /> Review Your Booking</h5>
                    <span className="badge bg-success-subtle text-success border border-success px-3 py-2 rounded-pill small">
                       <FaClock className="me-1"/> Price Locked
                    </span>
                </div>
                
                <div className="d-flex align-items-center bg-light p-3 rounded-3 mb-4">
                  <img src={hotelInfo.Images[0]} className="rounded-3 me-3" style={{width: '100px', height: '70px', objectFit: 'cover'}} alt="hotel" />
                  <div>
                    <h6 className="fw-bold mb-1">{hotelInfo.HotelName}</h6>
                    <small className="text-muted">{roomDetails.RoomTypeName}</small>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-md-6 border-end">
                    <h6 className="fw-bold small text-uppercase text-muted mb-3">Primary Contact</h6>
                    <p className="mb-1 fw-medium small"><FaEnvelope className="me-2 text-muted"/> {guestDetails.email}</p>
                    <p className="fw-medium small"><FaPhone className="me-2 text-muted"/> {guestDetails.phoneNumber}</p>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold small text-uppercase text-muted mb-3">Guests</h6>
                    {guestDetails.guests.map((g, i) => (
                      <div key={i} className="small mb-2 fw-medium">
                        <FaRegUser className="me-2 text-primary"/> {g.title} {g.firstName} {g.lastName} 
                        <span className="ms-2 badge bg-light text-dark fw-normal">{g.type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4 border-start border-4 border-primary">
              <div className="card-body p-4 d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="fw-bold mb-1"><HiOutlineWallet className="me-2 text-primary"/> Use Wallet Balance</h6>
                  <p className="small text-muted mb-0">Available: ₹{walletBalance}</p>
                </div>
                <div className="form-check form-switch">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    checked={useWallet} 
                    onChange={(e) => setUseWallet(e.target.checked)}
                    disabled={walletBalance <= 0}
                    style={{width: '2.5em', height: '1.25em', cursor: 'pointer'}}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card border-0 shadow-sm rounded-4 p-4 sticky-top" style={{top: '20px'}}>
              <h5 className="fw-bold mb-4">Price Summary</h5>
              
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted small">Booking Amount</span>
                <span className="fw-bold small">₹{bookingTotal}</span>
              </div>

              {useWallet && (
                <div className="d-flex justify-content-between mb-2 text-success fw-medium small">
                  <span>Wallet Deduction</span>
                  <span>- ₹{appliedWalletAmount}</span>
                </div>
              )}

              {gatewayFee > 0 && (
                <div className="d-flex justify-content-between mb-2 text-muted small">
                  <span>Platform Fee (2.7%)</span>
                  <span>₹{gatewayFee}</span>
                </div>
              )}

              <hr className="my-3" />
              
              <div className="d-flex justify-content-between h4 fw-bold text-primary mb-4">
                <span>Net Payable</span>
                <span>₹{finalNetPayable}</span>
              </div>

              <button 
                className="btn btn-primary btn-lg w-100 rounded-pill fw-bold mb-3 py-3 shadow-sm"
                onClick={handleFinalPayment}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <span className="spinner-border spinner-border-sm me-2"></span>
                ) : (
                  <><HiOutlineCreditCard className="me-2"/> CONFIRM & PAY</>
                )}
              </button>
              
              <div className="text-center">
                <small className="text-muted d-flex align-items-center justify-content-center" style={{fontSize: '11px'}}>
                  <HiOutlineShieldCheck className="me-1 text-success fs-6" /> 
                  Fees apply only to gateway payments.
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HotelCommit;